import "./index.css";
import { useEffect, useMemo, useState } from "react";

import EmptyState from "../empty-state";
import IdeaItem from "../idea-item";
import PostIdea from "../post-idea";
import LoadingState from "../loading-state";
import ExpandedIdeaItem from "../expanded-idea-item";
import Button from "../button";
import TimeSavedWidget from "../time-saved";

import { deleteIdea } from "../../functions/deleteIdea";
import { fetchStructuredIdea } from "../../functions/fetchStructureIdea";
import { getAuthCode } from "../../functions/getAuthCode";
import { downloadCsvBlob, rowsToCsv } from "../../functions/csvExport";

function IdeaFeedHolder({ feedItems, isLoaded, openSiteAdder }) {
  // Keep an internal copy so filters + deletions are local without mutating the parent list
  const [allItems, setAllItems] = useState([]);
  const [visibleItems, setVisibleItems] = useState([]);

  useEffect(() => {
    setAllItems(feedItems || []);
    setVisibleItems(feedItems || []);
  }, [feedItems]);

  // Expanded idea modal
  const [displayOfExpandedIdea, setDisplayOfExpandedIdea] = useState("none");
  const [expandedIdea, setExpandedIdea] = useState({
    title: "",
    structure: "",
    id: "",
    image: null,
    voiceName: null,
    voiceId: null,
    approved: null,
    scheduledDate: null,
    linkedinStatus: null,
    linkedinPostUrl: null,
    ideaType: null,
  });

  // Filters (ideas)
  const [filterForIdeas, setFilterForIdeas] = useState({
    approval: null,
    scheduled: null,
    posted: null,
    voices: [],
  });

  const uniqueNames = useMemo(() => {
    return [
      ...new Set((allItems || []).map((i) => i.voiceName).filter(Boolean)),
    ];
  }, [allItems]);

  useEffect(() => {
    const { approval, scheduled, posted, voices } = filterForIdeas;

    let filtered = allItems || [];

    if (approval !== null || scheduled !== null || posted !== null || voices.length > 0) {
      filtered = filtered.filter((item) => {
        if (approval === true && item.approved !== true) return false;
        if (approval === false && item.approved === true) return false;
        if (scheduled === true && !item.scheduledDate) return false;
        if (scheduled === false && item.scheduledDate) return false;
        if (posted === true && !item.posted) return false;
        if (posted === false && item.posted) return false;
        if (voices.length > 0 && !voices.includes(item.voiceName)) return false;
        return true;
      });
    }

    const sorted = [...filtered].sort((a, b) => {
      const da = a.scheduledDate ? new Date(a.scheduledDate) : null;
      const db = b.scheduledDate ? new Date(b.scheduledDate) : null;
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da - db;
    });

    setVisibleItems(sorted);
  }, [filterForIdeas, allItems]);

  const changeIdeaFilter = (ideaProperty, value) => {
    const next = {
      ...filterForIdeas,
      voices: [...filterForIdeas.voices],
    };

    if (ideaProperty === "voices") {
      const index = next.voices.indexOf(value);
      index === -1 ? next.voices.push(value) : next.voices.splice(index, 1);
    } else {
      next[ideaProperty] = next[ideaProperty] === value ? null : value;
    }

    setFilterForIdeas(next);
  };

  // Open expanded idea if URL has ?id=
  useEffect(() => {
    (async () => {
      try {
        const urlParams = new URLSearchParams(new URL(document.location).search);
        const id = urlParams.get("id");
        if (!id) return;

        const data = await fetchStructuredIdea(id);
        const found = data?.foundObject;

        if (!found) {
          const newUrl =
            window.location.protocol +
            "//" +
            window.location.host +
            window.location.pathname;
          window.history.replaceState({ path: newUrl }, "", newUrl);
          return;
        }

        if (found.ideaType === "blog") {
          setExpandedIdea(found);
          setDisplayOfExpandedIdea("block");
          return;
        }

        if (found.ideaType === "post") {
          setExpandedIdea({
            id: found.id,
            title: `${found.generatedFrom}`,
            generatedFrom: found.generatedFrom,
            stage: found.status,
            structure: found.content,
            scheduledDate: found.scheduledDate,
            image: found.image,
            pending:found.pending,
            approved: found.approved,
            voiceName: found.voiceName,
            voiceId: found.voice,
            linkedinStatus: found.linkedinStatus,
            linkedinPostUrl: found.linkedinPostUrl,
            ideaType: "post",
          });
          setDisplayOfExpandedIdea("block");
          return;
        }

        // unknown ideaType -> clean URL
        const newUrl =
          window.location.protocol +
          "//" +
          window.location.host +
          window.location.pathname;
        window.history.replaceState({ path: newUrl }, "", newUrl);
      } catch (e) {
        // If anything goes wrong, fail quietly and keep the page usable
        console.error(e);
      }
    })();
  }, []);

  const expandIdea = () => {
    setDisplayOfExpandedIdea("block");
  };

  const closeIdea = () => {
    setDisplayOfExpandedIdea("none");
    setExpandedIdea({
      title: "",
      structure: "",
      id: "",
      image: null,
      voiceName: null,
      voiceId: null,
      approved: null,
      scheduledDate: null,
      linkedinStatus: null,
      linkedinPostUrl: null,
      ideaType: null,
    });

    const newUrl =
      window.location.protocol +
      "//" +
      window.location.host +
      window.location.pathname;
    window.history.replaceState({ path: newUrl }, "", newUrl);
  };

  const handleGenerationComplete = (ideaId, content) => {
    setAllItems((current) =>
      current.map((item) =>
        item.id === ideaId
          ? { ...item, pending: false, content }
          : item
      )
    );
    setExpandedIdea((prev) => ({ ...prev, pending: false, structure: content }));
  };

  const clearImage = (ideaId) => {
    setExpandedIdea((prev) => ({ ...prev, image: null }));
    setAllItems((current) =>
      current.map((item) =>
        item.id === ideaId ? { ...item, image: null } : item
      )
    );
  };

  const handleDeleteRequest = (id) => {
    deleteIdea(id);

    // Remove from both sets so UI updates immediately
    setAllItems((prev) => prev.filter((x) => x.id !== id));
    setVisibleItems((prev) => prev.filter((x) => x.id !== id));
  };

  const downloadIdeas = async () => {
    if (!visibleItems || visibleItems.length === 0) return;

    const items = visibleItems;
    const includedFields = ["voiceName", "content", "scheduledDate", "image"];
    const header = includedFields;

    const rows = [
      header,
      ...items.map((row) => header.map((field) => row[field])),
    ];
    const csv = rowsToCsv(rows);
    downloadCsvBlob(
      `mako-post-ideas-${new Date().toISOString().split("T")[0]}.csv`,
      csv
    );
  };

  const autoSchedule = async () => {
    let authCode = await getAuthCode();

    for (let i = 0; i < (allItems || []).length; i++) {
      const idea = allItems[i];
      if (idea.stage === 1) {
        // weekdays in next 14 days
        const start = new Date();
        const end = new Date();
        end.setDate(end.getDate() + 14);

        const weekdays = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const day = d.getDay(); // 0=Sun, 6=Sat
          if (day !== 0 && day !== 6) weekdays.push(new Date(d));
        }

        const when = weekdays[Math.floor(Math.random() * weekdays.length)];
        const halfHours = [0, 30];
        when.setHours(
          9 + Math.floor(Math.random() * 9),
          halfHours[Math.floor(Math.random() * 2)],
          0, 0
        );

        const url =
          process.env.REACT_APP_BACKEND_URL +
          "/schedulePost?id=" +
          idea.id +
          "&date=" +
          encodeURIComponent(when.toString());

        await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ auth_code: authCode }),
        });

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "post_scheduled" });

        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }

    window.location.reload();
  };

  // ---------- Render ----------
  if (!isLoaded) return <LoadingState />;

  if (!visibleItems || visibleItems.length === 0 && filterForIdeas.voices.length === 0 && filterForIdeas.approval === null && filterForIdeas.scheduled === null && filterForIdeas.posted === null) {
    return (
      <EmptyState message="No ideas generated yet..." openSiteAdder={openSiteAdder} />
    );
  }

  const feedHTML = visibleItems.map((item) => {
    if (item.ideaType === "blog") {
      return (
        <IdeaItem
          key={item.id}
          date={item.dateAdded}
          title={item.title}
          status={item.stage}
          type={item.ideaType}
          generatedFrom={item.generatedFrom}
          id={item.id}
          voiceName={item.voiceName}
          approved={item.approved}
          scheduledDate={item.scheduledDate}
          display={displayOfExpandedIdea}
          expandIdea={expandIdea}
          SetExpandedIdea={setExpandedIdea}
          SetFeedItems={setVisibleItems}
          handleDeleteRequest={handleDeleteRequest}
        />
      );
    }

    if (item.ideaType === "post") {
      return (
        <PostIdea
          key={item.id}
          date={item.dateAdded}
          imageContent={item.imageContent}
          content={item.content}
          status={item.stage}
          type={item.ideaType}
          generatedFrom={item.generatedFrom}
          id={item.id}
          approved={item.approved}
          voiceName={item.voiceName}
          image={item.image}
          scheduledDate={item.scheduledDate}
          display={displayOfExpandedIdea}
          expandIdea={expandIdea}
          SetExpandedIdea={setExpandedIdea}
          SetFeedItems={setVisibleItems}
          handleDeleteRequest={handleDeleteRequest}
          voiceId={item.voice}
          linkedinStatus={item.linkedinStatus}
          postingToLinkedIn={item.postingToLinkedIn}
          posted={item.posted}
          pending={item.pending}
          linkedinPostUrl={item.linkedinPostUrl}
        />
      );
    }

    return null;
  });

  return (
    <>
      <div
        id="time-saved-holder"
        style={{
          display: displayOfExpandedIdea === "block" ? "none" : "flex",
        }}
      >
        <TimeSavedWidget totalIdeas={visibleItems} />
        <div id="button-holder-ideas">
          <Button
            text="Auto schedule"
            image="../images/schedule.png"
            addedFunction={autoSchedule}
          />
          <Button
            text="Download CSV"
            image="../images/download.png"
            addedFunction={downloadIdeas}
          />
        </div>
      </div>

      <div
        className="filter-for-feed"
        style={{
          display:
            displayOfExpandedIdea === "none" ? "flex" : "none",
        }}
      >
        <div className="filter-group">
          <span className="filter-group-label tiny-text">Approval</span>
          <div className="filter-group-chips">
            <div
              className={filterForIdeas.approval === true ? "selected-filter filter-item tiny-text" : "filter-item tiny-text"}
              onClick={() => changeIdeaFilter("approval", true)}
            >
              Approved
            </div>
            <div
              className={filterForIdeas.approval === false ? "selected-filter filter-item tiny-text" : "filter-item tiny-text"}
              onClick={() => changeIdeaFilter("approval", false)}
            >
              Not approved
            </div>
          </div>
        </div>

        <div className="filter-group-separator" />

        <div className="filter-group">
          <span className="filter-group-label tiny-text">Scheduled</span>
          <div className="filter-group-chips">
            <div
              className={filterForIdeas.scheduled === true ? "selected-filter filter-item tiny-text" : "filter-item tiny-text"}
              onClick={() => changeIdeaFilter("scheduled", true)}
            >
              Scheduled
            </div>
            <div
              className={filterForIdeas.scheduled === false ? "selected-filter filter-item tiny-text" : "filter-item tiny-text"}
              onClick={() => changeIdeaFilter("scheduled", false)}
            >
              Not scheduled
            </div>
          </div>
        </div>

        <div className="filter-group-separator" />

        <div className="filter-group">
          <span className="filter-group-label tiny-text">Posted</span>
          <div className="filter-group-chips">
            <div
              className={filterForIdeas.posted === true ? "selected-filter filter-item tiny-text" : "filter-item tiny-text"}
              onClick={() => changeIdeaFilter("posted", true)}
            >
              Posted
            </div>
            <div
              className={filterForIdeas.posted === false ? "selected-filter filter-item tiny-text" : "filter-item tiny-text"}
              onClick={() => changeIdeaFilter("posted", false)}
            >
              Not posted
            </div>
          </div>
        </div>

        {uniqueNames.length > 0 && (
          <>
            <div className="filter-group-separator" />
            <div className="filter-group">
              <span className="filter-group-label tiny-text">Voice</span>
              <div className="filter-group-chips">
                {uniqueNames.map((name) => (
                  <div
                    key={name}
                    className={filterForIdeas.voices.includes(name) ? "selected-filter filter-item tiny-text" : "filter-item tiny-text"}
                    onClick={() => changeIdeaFilter("voices", name)}
                  >
                    {name}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

      </div>

      <div className="feed-holder">
        {feedHTML}
        <ExpandedIdeaItem
          renderedFeedItems={visibleItems}
          setFeedItems={setVisibleItems}
          setAllItems={setAllItems}
          display={displayOfExpandedIdea}
          title={expandedIdea.title}
          structure={expandedIdea.structure}
          close={closeIdea}
          id={expandedIdea.id}
          pending={expandedIdea.pending}
          date={expandedIdea.scheduledDate}
          image={expandedIdea.image}
          voiceName={expandedIdea.voiceName}
          isIdeaApproved={expandedIdea.approved}
          linkedinStatus={expandedIdea.linkedinStatus}
          voiceId={expandedIdea.voiceId}
          clearImage={clearImage}
          linkedinPostUrl={expandedIdea.linkedinPostUrl}
          onGenerationComplete={handleGenerationComplete}
          ideaType={expandedIdea.ideaType}
        />
      </div>
    </>
  );
}

export default IdeaFeedHolder;